// Lib
import React from 'react';
import { X } from 'lucide-react';

// Components
import { CompanyInfoStep } from './steps/CompanyInfoStep';
import { IndustryStep } from './steps/IndustryStep';
import { GoalsStep } from './steps/GoalsStep';
import { BrandProgressBar } from './components/BrandProgressBar';
import { CreatorProgressBar } from './components/CreatorProgressBar';
import { CreatorInfoStep } from './steps/CreatorInfoStep';
import { ShippingStep } from './steps/ShippingStep';
import { RoleSelector } from './components/RoleSelector';
import { Button } from '@components/ui/button';
import { cn } from '@components/lib/utils';

// Hooks
import { useGeoData } from '../model/useGeoData';
import { useCreateOrganization } from '../model/useCreateOrganization';

const BTN = 'cursor-pointer disabled:cursor-not-allowed';

/* eslint-disable max-lines-per-function, complexity -- one modal with a header/body/footer
 * frame around a role picker + per-role step flow; flat reads clearer than
 * splitting the shared chrome from the step switch. */
const CreateOrganizationModal: React.FC = () => {
  const {
    step,
    setStep,
    role,
    setRole,
    selectedRole,
    setSelectedRole,
    loading,
    answers,
    error,
    onCreateOrganization,
    onCreateCreatorOrganization,
    onChange,
    onSetError,
    closeModal,
  } = useCreateOrganization();

  const { countries, states, cities } = useGeoData(answers);

  const roleChosen = Boolean(role);
  const isBrand = role === 'brand';
  const lastStep = isBrand ? 2 : 1;

  const primaryLabel = !roleChosen
    ? 'Continue'
    : step === lastStep
    ? 'Get started'
    : 'Next';
  const primaryDisabled = !roleChosen ? !selectedRole : loading;

  const onPrimary = (): void => {
    if (!roleChosen) {
      setRole(selectedRole);
      return;
    }
    if (!answers?.name) {
      onSetError('name', 'Empty field');
      return;
    }
    if (step < lastStep) {
      setStep((prev) => prev + 1);
    } else if (isBrand) {
      onCreateOrganization();
    } else {
      onCreateCreatorOrganization();
    }
  };

  // Back on the first step returns to the type picker; deeper steps go up one.
  const onBack = (): void => {
    if (step === 0) setRole('');
    else setStep((prev) => prev - 1);
  };

  return (
    <div className="flex w-full flex-col">
      {/* Header — a real row so the close button never overlaps the progress
          bar below; title only on the type picker (steps carry their own). */}
      <div className="flex items-center justify-between gap-2 px-6 pt-5">
        {!roleChosen ? (
          <h1 className="text-lg font-bold text-foreground">
            Select organization type
          </h1>
        ) : (
          <span />
        )}
        <button
          type="button"
          onClick={closeModal}
          aria-label="Close"
          className="-mr-1 flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-md text-marble-bg-500 hover:bg-marble-bg-100 hover:text-foreground"
        >
          <X className="size-4" />
        </button>
      </div>

      {roleChosen && (
        <div className="px-6 pt-3">
          {isBrand ? (
            <BrandProgressBar step={step} />
          ) : (
            <CreatorProgressBar step={step} />
          )}
        </div>
      )}

      {/* Body */}
      <div className="px-6 pb-8 pt-4">
        {!roleChosen ? (
          <div className="pt-2 pb-2">
            <RoleSelector
              selectedRole={selectedRole}
              setSelectedRole={setSelectedRole}
            />
          </div>
        ) : (
          <div className="mx-auto w-full max-w-[352px]">
            {isBrand ? (
              <>
                {step === 0 && (
                  <CompanyInfoStep
                    answers={answers}
                    error={error}
                    onChange={onChange}
                    onSetError={onSetError}
                  />
                )}
                {step === 1 && (
                  <IndustryStep
                    answers={answers}
                    error={error}
                    onChange={onChange}
                  />
                )}
                {step === 2 && (
                  <GoalsStep
                    answers={answers}
                    error={error}
                    onChange={onChange}
                    onSetError={onSetError}
                  />
                )}
              </>
            ) : (
              <>
                {step === 0 && (
                  <CreatorInfoStep
                    answers={answers}
                    error={error}
                    onChange={onChange}
                    onSetError={onSetError}
                  />
                )}
                {step === 1 && (
                  <ShippingStep
                    answers={answers}
                    error={error}
                    onChange={onChange}
                    onSetError={onSetError}
                    countries={countries}
                    states={states}
                    cities={cities}
                  />
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Footer — one consistent Back / primary pair for every step. */}
      <div className="mt-4 flex items-center justify-between gap-3 border-t border-border px-6 py-4">
        <Button
          variant="ghost"
          onClick={onBack}
          className={cn(BTN, !roleChosen && 'invisible')}
        >
          Back
        </Button>
        <Button
          onClick={onPrimary}
          disabled={primaryDisabled}
          loading={loading}
          className={BTN}
        >
          {primaryLabel}
        </Button>
      </div>
    </div>
  );
};
/* eslint-enable max-lines-per-function, complexity */

export default CreateOrganizationModal;
